import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { BookOpen, Plus, Edit2, Trash2, ExternalLink, X, Image } from 'lucide-react';
import { PublicationFormat } from '@ftt/shared';

export const AdminCmsPublicationsPage: React.FC = () => {
  const [editingPub, setEditingPub] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [author, setAuthor] = useState('');
  const [category, setCategory] = useState('');
  const [format, setFormat] = useState<PublicationFormat>('HARDCOVER');
  const [coverImage, setCoverImage] = useState('');
  const [description, setDescription] = useState('');
  const [isbn, setIsbn] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [bookstoreUrl, setBookstoreUrl] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);

  const queryClient = useQueryClient();

  const { data: publicationsRes, isLoading } = useQuery({
    queryKey: ['admin-publications'],
    queryFn: () => api.get('/public/publications').then((res) => res.data.data),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title,
        slug: slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        author,
        category: category || 'Theology & Biblical Studies',
        format,
        coverImage: coverImage || 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&q=80&w=800',
        publicationDate: new Date().toISOString(),
        description,
        isbn: isbn || null,
        publisher: 'For The Truth Publishing',
        excerpt: excerpt || null,
        bookstoreUrl: bookstoreUrl || null,
        isFeatured,
        isPublished: true,
      };

      if (editingPub?.id) {
        return api.put(`/admin/publications/${editingPub.id}`, payload);
      } else {
        return api.post('/admin/publications', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-publications'] });
      queryClient.invalidateQueries({ queryKey: ['public-publications'] });
      setEditingPub(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/admin/publications/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-publications'] });
      queryClient.invalidateQueries({ queryKey: ['public-publications'] });
    },
  });

  const publications = publicationsRes || [];

  const openEditor = (pub?: any) => {
    if (pub) {
      setEditingPub(pub);
      setTitle(pub.title);
      setSlug(pub.slug);
      setAuthor(pub.author);
      setCategory(pub.category);
      setFormat(pub.format);
      setCoverImage(pub.coverImage);
      setDescription(pub.description);
      setIsbn(pub.isbn || '');
      setExcerpt(pub.excerpt || '');
      setBookstoreUrl(pub.bookstoreUrl || '');
      setIsFeatured(pub.isFeatured);
    } else {
      setEditingPub({ id: '' });
      setTitle('');
      setSlug('');
      setAuthor('');
      setCategory('Theology & Biblical Studies');
      setFormat('HARDCOVER');
      setCoverImage('');
      setDescription('');
      setIsbn('');
      setExcerpt('');
      setBookstoreUrl('https://forthetruth.in/books');
      setIsFeatured(false);
    }
  };

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              CATALOG & CMS
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <BookOpen className="w-7 h-7 text-[#FF6B4A]" />
            <span>Published Works CMS</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Maintain published books, cover art, ISBNs, excerpts, and bookstore cross-links.
          </p>
        </div>

        <button
          onClick={() => openEditor()}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md transition-all self-start sm:self-auto cursor-pointer"
        >
          <Plus className="w-4 h-4 text-[#FF6B4A]" />
          <span>Add New Publication</span>
        </button>
      </div>

      {/* 2. Publications Grid */}
      {isLoading ? (
        <div className="p-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">Loading catalog...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {publications.map((pub: any) => (
            <div
              key={pub.id}
              className="p-6 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 space-y-4 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="space-y-3">
                <div className="aspect-[4/5] rounded-xl overflow-hidden bg-slate-100 border border-slate-200/80">
                  <img src={pub.coverImage} alt={pub.title} className="w-full h-full object-cover" />
                </div>
                <div>
                  <span className="text-[10px] font-mono font-bold uppercase text-brand-700 block">{pub.category}</span>
                  <h3 className="font-serif text-lg font-bold text-slate-900 leading-snug">{pub.title}</h3>
                  <p className="text-xs text-slate-600">By {pub.author} &bull; {pub.format}</p>
                </div>
                {pub.isbn && <div className="text-[11px] font-mono text-slate-500">ISBN: {pub.isbn}</div>}
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                <button
                  onClick={() => openEditor(pub)}
                  className="px-3.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <Edit2 className="w-3.5 h-3.5 text-[#FF6B4A]" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => deleteMutation.mutate(pub.id)}
                  className="px-3.5 py-1.5 rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 font-bold inline-flex items-center gap-1.5 border border-rose-200 transition-colors cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 3. Editor Modal */}
      {editingPub && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-brand-700 font-bold">CMS CURATION</span>
                <h3 className="font-serif text-xl font-bold text-slate-900">
                  {editingPub.id ? 'Edit Publication' : 'Add New Book'}
                </h3>
              </div>
              <button 
                onClick={() => setEditingPub(null)} 
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4 text-xs font-sans">
              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Book Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  placeholder="e.g. The Pillars of Sovereign Grace"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Author Name *</label>
                  <input
                    type="text"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Format</label>
                  <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value as any)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  >
                    <option value="HARDCOVER">HARDCOVER</option>
                    <option value="PAPERBACK">PAPERBACK</option>
                    <option value="EBOOK">EBOOK</option>
                    <option value="AUDIO">AUDIO</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Category</label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">ISBN (Optional)</label>
                  <input
                    type="text"
                    value={isbn}
                    onChange={(e) => setIsbn(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                    placeholder="978-1-..."
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Cover Image URL</label>
                <input
                  type="url"
                  value={coverImage}
                  onChange={(e) => setCoverImage(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden font-mono text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Bookstore Purchase Link</label>
                <input
                  type="url"
                  value={bookstoreUrl}
                  onChange={(e) => setBookstoreUrl(e.target.value)}
                  placeholder="https://forthetruth.in/books/..."
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden font-mono text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">Synopsis &amp; Description</label>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                onClick={() => setEditingPub(null)}
                className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 font-bold transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={!title || !author || saveMutation.isPending}
                className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md transition-colors cursor-pointer disabled:opacity-50"
              >
                Save Publication
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
